import { Loader } from "@tql/ui-components-react/kendo/indicators";
import { useState } from "react";

import { useShipmentNotifications } from "~/features/shipment-management/hooks/use-shipment-notifications.ts";
import { TraxGrid } from "~/shared/components/trax-grid/trax-grid.tsx";
import { TraxModal } from "~/shared/components/trax-modal/trax-modal.tsx";
import type { ShipmentException } from "~/shared/schemas/shipment-management-schema.ts";
import { camelToTitle } from "~/shared/utils/string-fns.ts";

import type { AugmentedShipment } from "../../types/augmented-shipment";
import { usePostExceptionAcknowledged } from "./hooks/use-post-exception-acknowledged.ts";

export const ExceptionModal = ({
	isOpen = false,
	onClose,
	exceptionType,
	shipment,
}: {
	isOpen?: boolean;
	onClose?: () => void;
	exceptionType:
		| "immediateActionExceptions"
		| "needsAcknowledgedExceptions"
		| null;
	shipment: AugmentedShipment;
}) => {
	const { postExceptionAcknowledgedAsync, isPostingExceptionAcknowledged } =
		usePostExceptionAcknowledged();
	const { showError } = useShipmentNotifications();
	const [selectedException, setSelectedException] =
		useState<ShipmentException | null>(null);

	const handleAcknowledge = async () => {
		if (selectedException) {
			try {
				await postExceptionAcknowledgedAsync(selectedException.id ?? 0);
				setSelectedException(null);
				onClose?.();
			} catch (error) {
				console.error(error);
				showError("Failed to acknowledge exception. Please try again.");
			}
		}
	};

	if (!isOpen || exceptionType === null) {
		return null;
	}

	const exceptionRows = (shipment?.[exceptionType] ?? []).map(
		(exception, index) => ({
			...exception,
			rowKey: exception.id ?? `no-id-${index}`,
		}),
	);

	return (
		<TraxModal
			confirmDisabled={!selectedException?.id}
			confirmText={"Acknowledge"}
			onCancel={onClose}
			onClose={onClose}
			onConfirm={() => void handleAcknowledge()}
			showButtonsDisabled={isPostingExceptionAcknowledged}
			title={`${camelToTitle(exceptionType)} Exceptions`}
			type={"confirmation"}
			width={"25%"}
		>
			{isPostingExceptionAcknowledged ? (
				<div style={{ display: "flex", justifyContent: "center" }}>
					<Loader
						size={"medium"}
						themeColor={"primary"}
						type={"infinite-spinner"}
					/>
				</div>
			) : (
				<TraxGrid
					columns={[
						{
							field: "message",
							title: "Exception",
							sortable: false,
							editable: false,
						},
					]}
					data={exceptionRows}
					dataItemKey={"rowKey"}
					onRowClick={(event) =>
						setSelectedException(event.dataItem as ShipmentException)
					}
				/>
			)}
		</TraxModal>
	);
};
